import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDay, fmtPct } from "@/lib/api";

const STATE = {
  up_to_date: { word: "Filled", colour: "hsl(var(--healthy))" },
  behind: { word: "Behind", colour: "hsl(var(--watch))" },
  quiet: { word: "Gone quiet", colour: "hsl(var(--at-risk))" },
  never: { word: "Never filled", colour: "hsl(var(--at-risk))" },
  none: { word: "None on watch", colour: "hsl(var(--muted-foreground))" },
};

const said = (row) => {
  if (!row.tracked && row.tracked !== undefined && row.state === "none")
    return "nothing on compliance watch";
  if (row.days_since === null || row.days_since === undefined) return "no day ever filled";
  if (row.days_since <= 0) return "filled today";
  if (row.days_since === 1) return "filled yesterday";
  return `${row.days_since} days since the last reading`;
};

/**
 * Compliance data health — who is filling their readings, and who has gone quiet.
 *
 * Read from the one compliance store, so a unit that is up to date here is up to date on the
 * odometer. The two thresholds are SDTD's, from the Settings screen.
 */
export const ComplianceHealth = ({ plants = "" }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");

  const load = useCallback(() => {
    api
      .get("/compliance/health", { params: { plants } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [plants]);

  useEffect(load, [load]);

  if (!data)
    return (
      <div className="flex h-24 items-center justify-center border border-border bg-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;
  const shown = data.units;
  const chosen = data.units.find((u) => u.plant === open);

  return (
    <div data-testid="compliance-health">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Compliance data health
          </div>
          <h2 className="mt-1 font-display text-lg font-bold tracking-tight">
            Who filled yesterday, and who has gone quiet
          </h2>
        </div>
        <p
          className="max-w-xl text-[11px] leading-relaxed text-muted-foreground"
          data-testid="health-summary"
        >
          <b className="font-data text-foreground">{t.units_up_to_date}</b> of{" "}
          {t.units_watching} unit(s) on watch are up to date as on {fmtDay(data.as_on)} ·{" "}
          <span style={{ color: STATE.behind.colour }}>{t.units_behind} behind</span> ·{" "}
          <span style={{ color: STATE.quiet.colour }}>{t.units_quiet} gone quiet</span>
          {t.units_never ? (
            <>
              {" · "}
              <span style={{ color: STATE.never.colour }}>
                {t.units_never} never filled at all
              </span>
            </>
          ) : null}
          . A unit is behind once {data.thresholds.behind_days} day(s) have passed with no
          reading, and quiet after {data.thresholds.quiet_days}.
        </p>
      </div>

      <div
        className="mt-4 grid gap-px bg-border grid-cols-2 sm:grid-cols-4 lg:grid-cols-7"
        data-testid="health-strip"
      >
        {shown.map((u) => {
          const tone = STATE[u.state] || STATE.none;
          const on = open === u.plant;
          return (
            <button
              key={u.plant}
              type="button"
              data-testid={`health-unit-${u.plant}`}
              onClick={() => setOpen(on ? "" : u.plant)}
              title={`${u.name} — ${said(u)}`}
              className={`bg-card p-3 text-left transition-colors duration-200 hover:bg-muted ${
                on ? "ring-1 ring-inset ring-foreground" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-display text-sm font-black tracking-tight">{u.plant}</span>
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: tone.colour }}
                />
              </div>
              <div
                className="font-data mt-1 text-[10px] font-bold uppercase tracking-wider"
                style={{ color: tone.colour }}
                data-testid={`health-state-${u.plant}`}
              >
                {tone.word}
              </div>
              <div className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
                {u.tracked ? `${u.tracked} on watch · ${said(u)}` : "nothing on watch"}
              </div>
              <ChevronDown
                className={`mt-1 h-3 w-3 text-muted-foreground transition-transform duration-200 ${
                  on ? "rotate-180" : ""
                }`}
              />
            </button>
          );
        })}
      </div>

      {chosen && (
        <div
          className="mt-px border border-border bg-card p-4"
          data-testid={`health-rows-${chosen.plant}`}
        >
          <div className="label-caps">
            {chosen.plant} · {chosen.name} — last day filled, usecase by usecase
          </div>
          {chosen.rows.length ? (
            <div className="mt-3 divide-y divide-border">
              {chosen.rows.map((row) => {
                const tone = STATE[row.state] || STATE.none;
                return (
                  <button
                    key={row.project_id}
                    type="button"
                    data-testid={`health-usecase-${row.code}`}
                    onClick={() =>
                      navigate(
                        `/compliance-data?q=${encodeURIComponent(row.code || "")}&plant=${chosen.plant}`,
                      )
                    }
                    className="flex w-full flex-wrap items-center justify-between gap-3 border-l-2 border-transparent py-2.5 pl-3 text-left transition-colors duration-200 hover:border-primary hover:bg-muted"
                  >
                    <span className="min-w-0">
                      <span className="font-data block text-[10px] text-muted-foreground">
                        {row.code} · {row.kpi_name || "KPI not named"}
                        {row.visible ? "" : " · off the dashboard"}
                      </span>
                      <span className="block truncate text-sm font-semibold">{row.title}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-5">
                      <span className="text-right">
                        <span className="font-data block text-[9px] uppercase tracking-widest text-muted-foreground">
                          Last filled
                        </span>
                        <span className="font-data block text-xs font-bold tabular-nums">
                          {row.last_day ? fmtDay(row.last_day) : "never"}
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="font-data block text-[9px] uppercase tracking-widest text-muted-foreground">
                          Days on record
                        </span>
                        <span className="font-data block text-xs font-bold tabular-nums">
                          {row.days}
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="font-data block text-[9px] uppercase tracking-widest text-muted-foreground">
                          Cumulative
                        </span>
                        <span className="font-data block text-xs font-bold tabular-nums">
                          {fmtPct(row.cumulative_pct)}
                        </span>
                      </span>
                      <span
                        className="font-data border px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider"
                        style={{ borderColor: tone.colour, color: tone.colour }}
                      >
                        {said(row)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              {chosen.plant} has nothing on compliance watch yet. A usecase goes on watch from the
              hot trial onwards, on the Compliance Data screen.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
